import React, { useState, useCallback } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { emitOnboardingStep } from '../../../onboarding/onboardingEvents';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useNavigate } from 'react-router-dom';

/**
 * Severity-to-MUI-severity mapping and custom colors.
 */
const SEVERITY_CONFIG = {
  critical: { muiSeverity: 'error', color: '#d32f2f', bgColor: '#fdecea', label: 'Critical' },
  high: { muiSeverity: 'error', color: '#d32f2f', bgColor: '#fdecea', label: 'High' },
  warning: { muiSeverity: 'warning', color: '#ed6c02', bgColor: '#fff4e5', label: 'Warning' },
  medium: { muiSeverity: 'warning', color: '#ed6c02', bgColor: '#fff4e5', label: 'Medium' },
  info: { muiSeverity: 'info', color: '#0288d1', bgColor: '#e5f6fd', label: 'Info' },
  low: { muiSeverity: 'info', color: '#0288d1', bgColor: '#e5f6fd', label: 'Info' },
};

function getConfig(severity) {
  return SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
}

/**
 * A single risk alert card with expandable details.
 */
function RiskAlertItem({ alert, onAcknowledge, onDismiss, showIndustryRefs = true }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const config = getConfig(alert.severity);

  // Onboarding: any way of engaging with the alert counts as "read the alert" —
  // expanding it, acknowledging it, or opening its case study. Previously only
  // the small chevron fired the event, so most real interactions were missed.
  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      if (!prev) emitOnboardingStep('read-the-alert');
      return !prev;
    });
  }, []);

  const handleCaseStudyClick = useCallback(
    (e) => {
      e.preventDefault();
      if (alert.caseStudyId) {
        emitOnboardingStep('read-the-alert');
        navigate(`/drew/case-studies/${alert.caseStudyId}`);
      }
    },
    [alert.caseStudyId, navigate],
  );

  return (
    <Alert
      severity={config.muiSeverity}
      sx={{
        mb: 1,
        borderLeft: `4px solid ${config.color}`,
        '& .MuiAlert-message': { width: '100%' },
      }}
      action={
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <IconButton size="small" title="Expand details" onClick={toggleExpanded}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <IconButton
            size="small"
            title="Dismiss alert"
            onClick={() => onDismiss(alert)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      }
    >
      <AlertTitle
        onClick={toggleExpanded}
        sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
      >
        {alert.title || 'Risk Alert'}
        <Typography
          component="span"
          variant="caption"
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: config.color,
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.7rem',
            textTransform: 'uppercase',
          }}
        >
          {config.label}
        </Typography>
      </AlertTitle>

      {/* Summary line — always visible */}
      {alert.summary && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {alert.summary}
        </Typography>
      )}

      {/* Case study link */}
      {alert.caseStudyId && alert.caseStudyTitle && (showIndustryRefs || alert.caseStudyOrganizationId) && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Similar to:{' '}
          <Link
            href="#"
            onClick={handleCaseStudyClick}
            underline="hover"
            sx={{ fontWeight: 500, cursor: 'pointer' }}
          >
            {alert.caseStudyTitle}
          </Link>
        </Typography>
      )}

      {/* Expandable details */}
      <Collapse in={expanded}>
        {/* Full description */}
        {alert.description && (
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            {alert.description}
          </Typography>
        )}

        {/* Recommended actions */}
        {alert.recommendedActions && alert.recommendedActions.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Recommended Actions
            </Typography>
            <List dense disablePadding>
              {alert.recommendedActions.map((action, idx) => (
                <ListItem key={idx} disableGutters sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <FiberManualRecordIcon sx={{ fontSize: 8, color: config.color }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={action}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Acknowledge button */}
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            color={config.muiSeverity === 'error' ? 'error' : config.muiSeverity}
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => onAcknowledge(alert)}
          >
            Acknowledge
          </Button>
          {alert.caseStudyId && (
            <Button
              size="small"
              variant="text"
              endIcon={<ArrowForwardIcon />}
              onClick={handleCaseStudyClick}
            >
              View Case Study
            </Button>
          )}
        </Box>
      </Collapse>
    </Alert>
  );
}

/**
 * RiskAlertBanner — renders a stack of active risk alerts for an organization.
 *
 * Props:
 *   alerts: Array of risk alert objects from useRiskAlerts hook
 *   onAcknowledge: (alert) => void — called when user acknowledges an alert
 *   loading: boolean
 */
export default function RiskAlertBanner({ alerts = [], onAcknowledge, loading, showIndustryRefs = true }) {
  const [dismissConfirm, setDismissConfirm] = useState(null);
  const [dismissed, setDismissed] = useState([]);
  // UI review 2026-09-21 §5: several full banners took the first 170 px of the dashboard on every
  // visit. With more than one alert the stack starts folded into a one-line strip; the strip keeps
  // the top severity's colour and expands in place. A single alert still shows in full.
  const [expanded, setExpanded] = useState(false);

  const handleDismissRequest = useCallback((alert) => {
    setDismissConfirm(alert);
  }, []);

  const handleDismissConfirm = useCallback(() => {
    if (dismissConfirm) {
      // Acknowledge in Firestore + hide locally
      if (onAcknowledge) onAcknowledge(dismissConfirm);
      setDismissed((prev) => [...prev, dismissConfirm.id]);
    }
    setDismissConfirm(null);
  }, [dismissConfirm, onAcknowledge]);

  const handleDismissCancel = useCallback(() => {
    setDismissConfirm(null);
  }, []);

  const handleAcknowledge = useCallback(
    (alert) => {
      // Acknowledging implies the alert was read.
      emitOnboardingStep('read-the-alert');
      if (onAcknowledge) onAcknowledge(alert);
      setDismissed((prev) => [...prev, alert.id]);
    },
    [onAcknowledge],
  );

  // Filter out locally dismissed alerts
  const visibleAlerts = alerts.filter((a) => !dismissed.includes(a.id));

  if (loading || visibleAlerts.length === 0) return null;

  const folded = visibleAlerts.length > 1 && !expanded;
  const SEV_RANK = ['critical', 'high', 'warning', 'medium', 'info', 'low'];
  const top = [...visibleAlerts].sort((a, b) => SEV_RANK.indexOf(a.severity) - SEV_RANK.indexOf(b.severity))[0];
  const topCfg = getConfig(top?.severity);
  const criticalCount = visibleAlerts.filter((a) => ['critical', 'high'].includes(a.severity)).length;

  return (
    <Box sx={{ mb: 2 }}>
      {folded ? (
        <Alert
          severity={topCfg.muiSeverity}
          sx={{ borderLeft: `4px solid ${topCfg.color}`, alignItems: 'center', '& .MuiAlert-message': { width: '100%', py: 0.5 } }}
          action={
            <Button size="small" color="inherit" endIcon={<ExpandMoreIcon fontSize="small" />} onClick={() => setExpanded(true)} sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
              Show
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {visibleAlerts.length} OE health alerts{criticalCount ? ` · ${criticalCount} critical` : ''}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }}>
              — {top.title}
            </Typography>
          </Typography>
        </Alert>
      ) : (
      <Stack spacing={1}>
        {visibleAlerts.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" endIcon={<ExpandLessIcon fontSize="small" />} onClick={() => setExpanded(false)} sx={{ fontWeight: 700 }}>
              Fold alerts
            </Button>
          </Box>
        )}
        {visibleAlerts.map((alert) => (
          <RiskAlertItem
            key={alert.id}
            alert={alert}
            onAcknowledge={handleAcknowledge}
            onDismiss={handleDismissRequest}
            showIndustryRefs={showIndustryRefs}
          />
        ))}
      </Stack>
      )}

      {/* Dismiss confirmation dialog */}
      <Dialog open={!!dismissConfirm} onClose={handleDismissCancel}>
        <DialogTitle>Dismiss Alert?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to dismiss this alert? It will be hidden from your view
            only — it stays active for the rest of your organization until it is resolved.
          </DialogContentText>
          {dismissConfirm && (
            <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
              {dismissConfirm.title}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDismissCancel}>Cancel</Button>
          <Button onClick={handleDismissConfirm} color="error" variant="contained">
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
